import { zodResolver } from '@hookform/resolvers/zod';
import {
  Banner,
  Box,
  Button,
  Link,
  ResponsiveSection,
  Spinner,
  Stack,
  Text,
} from '@stitch-fix/mode-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Layout } from '../../../components/Layout';
import { SEOData } from '../../../components/SEOData';
import { useRiskyLogin } from '../../../hooks/useRiskyLogin';
import { getParams, isNonEmptyString } from '../../../utils/helpers';
import { browserLogger } from '../../../utils/loggers/browser';
import { requestPasswordReset } from '../../../utils/requests/clientAuth/password';
import { isRequestPasswordResetError } from '../../../utils/requests/clientAuth/types';
import { ControlledTextField } from '../../../components/Forms';
import { type EmailSchema, emailSchema } from '../../../utils/zod/emailSchema';

const displaySuccessMessage = ({
  successMessage,
  clearSuccessMessage,
}: {
  successMessage: string;
  clearSuccessMessage: () => void;
}) => (
  <Stack mt={2} spacing={2} data-testid="success-message">
    <Banner variant="success-border" onClose={clearSuccessMessage}>
      {successMessage}
    </Banner>
    <Text setting="body-small">
      Didn’t receive an email?&nbsp;
      <Link as="button" onClick={clearSuccessMessage}>
        Try again
      </Link>
    </Text>
  </Stack>
);

const PasswordNew = () => {
  const loginEventId = getParams()?.get('login_event_id') || '';
  const [confirmCompromisedLogin] = useRiskyLogin({
    loginEventId,
    status: 'compromised',
  });

  useEffect(() => {
    if (isNonEmptyString(loginEventId)) confirmCompromisedLogin();
  }, [loginEventId, confirmCompromisedLogin]);

  const {
    control,
    handleSubmit,
    setError,
    formState: { isSubmitting },
  } = useForm<EmailSchema>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: '',
    },
  });

  const [successMessage, setSuccessMessage] = useState<string | undefined>();

  const handleOnSubmit = async (data: EmailSchema) => {
    try {
      const { data: requestPasswordResetResponse } = await requestPasswordReset(
        data.email,
      );

      setSuccessMessage(requestPasswordResetResponse.message);
    } catch (error) {
      if (isRequestPasswordResetError(error)) {
        setError('email', { message: error.response.data.message });
      } else {
        setError('email', { message: 'Unknown error. Please try again.' });
        browserLogger({
          level: 'warn',
          message: 'Request password reset email error',
          team: 'client-onboarding',
          context: { error },
        });
      }
    }
  };

  return (
    <Layout viewEventTrackingName="password_reset">
      <ResponsiveSection mb={4} mt={2} width="xxlarge" minHeight="auto">
        <SEOData
          canonicalUrl="https://www.stitchfix.com/password/new"
          title="Password Recovery | Personal Styling for Women, Men &amp; Kids"
          robots="noindex, nofollow"
        />
        <Box maxWidth={33} mx="auto">
          <Box mb={2}>
            <Text setting="title-large" as="h1" spacingBottom>
              Forgot Your Password?
            </Text>
            <Text setting="subtitle-small" as="h2">
              No worries, we’ll get you back on track.
            </Text>
          </Box>

          {isNonEmptyString(successMessage) ? (
            displaySuccessMessage({
              successMessage,
              clearSuccessMessage: () => setSuccessMessage(undefined),
            })
          ) : (
            <form
              method="post"
              aria-label="forgot password"
              onSubmit={handleSubmit(data => handleOnSubmit(data))}
            >
              <ControlledTextField
                control={control}
                name="email"
                label="Email"
                disabled={isSubmitting}
                autoFocus
              />
              <Box maxWidth={21.25} pt={1}>
                <Button
                  type="submit"
                  width="fit"
                  startIcon={
                    isSubmitting ? (
                      <Spinner size="xsmall" variant="process" />
                    ) : null
                  }
                  disabled={isSubmitting}
                >
                  Reset password
                </Button>
              </Box>
            </form>
          )}
        </Box>
      </ResponsiveSection>
    </Layout>
  );
};

export default PasswordNew;
